import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  ContentCopy,
  MoreVert as MoreVertIcon
} from '@material-ui/icons'
import { deleteTalkById, getConferenceTalks } from 'store/talk'
import { renderCol } from 'utils/renderColumns'
import ResourceNotFound from 'components/ResourceNotFound'
import getTableOptions from 'utils/getTableOptions'
import BasicTable from 'components/BasicTable'
import Wrapper from 'components/Wrapper'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import { useHistory } from 'react-router-dom'
import ConfirmModal from 'components/ConfirmationModal'
import { toast } from 'utils/notify'

const app_base_url = window.location.origin

const TalkListing = ({ isPoster = false }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [confTalks, setConfTalks] = useState([])
  const [deletedTalkId, setDeletedTalkId] = useState('')
  const [performingAction, setPerformingAction] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [currentTalk, setCurrentTalk] = useState(null)
  const { conference } = useSelector((state) => state.conference)

  const fetchTalks = async () => {
    try {
      setLoading(true)
      const talks = await getConferenceTalks(conference, isPoster)
      setConfTalks(talks)
    } catch (error) {
      console.error('Error fetching talks:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (conference?.id) {
      fetchTalks()
    }
  }, [conference])

  const handleDeleteTalk = async () => {
    try {
      setPerformingAction(true)
      await dispatch(deleteTalkById(deletedTalkId))
      const talks = await getConferenceTalks(conference, isPoster)
      setConfTalks(talks)
    } catch (error) {
      console.error('Error deleting talk:', error)
    } finally {
      setPerformingAction(false)
      setDeletedTalkId('')
    }
  }

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ width: '40px' }}>{value}</div>
      }
    },
    'Parent Event/Sub Event',
    `${isPoster ? 'Poster #' : 'Abstract #'}`,
    'Title',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    {
      name: 'Building',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    'Speakers',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]

  const formatTableData = (talks) =>
    talks?.map((talk) => {
      const talkNo =
        talk.isAbstract && talk.isPoster
          ? `${conference?.posterPrefix ? `${conference?.posterPrefix} - ` : ''}${
              talk.posterNumber
            }`
          : talk.isAbstract
          ? `${conference?.abstractPrefix ? `${conference?.abstractPrefix} - ` : ''}${
              talk.abstractNumber
            }`
          : ''
      return [
        talk?.srNo,
        <div>
          {talk?.parentEvent?.title}
          {/*<span style={{ marginLeft: '4px', fontSize: '12px', color: '#8b8b8b' }}>*/}
          {/*  ({talk?.parentEvent?.entity})*/}
          {/*</span>*/}
        </div>,
        talkNo,
        talk.title,
        talk.startDate,
        talk.endDate,
        talk?.buildingText,
        talk?.speakerNames,
        // talk?.pollId ? 'Yes' : 'No',
        <div style={{ textAlign: 'center' }}>
          <IconButton
            size="small"
            onClick={(event) => {
              setMenuAnchorEl(event.currentTarget)
              setCurrentTalk(talk)
            }}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl) && currentTalk?.id === talk?.id}
            onClose={() => {
              setMenuAnchorEl(null)
              setCurrentTalk(null)
            }}>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentTalk(null)
                history.push(`${location.pathname}/edit/${talk.id}`)
              }}>
              <EditIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentTalk(null)
                setDeletedTalkId(talk?.id)
              }}>
              <DeleteIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
              Delete
            </MenuItem>
            {(talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : [])).map((pollId, index) => (
              <MenuItem
                key={pollId}
                onClick={() => {
                  setMenuAnchorEl(null)
                  setCurrentTalk(null)
                  navigator.clipboard.writeText(`${app_base_url}/polls/${pollId}`)
                  toast.info(`Poll ${index + 1} link copied to clipboard`, {
                    duration: 1000,
                    isClosable: true
                  })
                }}>
                <ContentCopy fontSize="small" color="action" style={{ marginRight: 10 }} />
                Copy Poll {index + 1} Link
              </MenuItem>
            ))}
          </Menu>
        </div>
      ]
    })

  const options = getTableOptions()
  return (
    <>
      <Wrapper inProgress={loading}>
        {confTalks?.length ? (
          <BasicTable data={formatTableData(confTalks)} columns={tableHead} options={options} />
        ) : (
          <ResourceNotFound message={`No ${isPoster ? 'Poster' : 'Talk'} available`} />
        )}
      </Wrapper>

      <ConfirmModal
        dialogTitle={`Delete ${isPoster ? 'Poster' : 'Talk'}`}
        dialogText={`Are you sure you want to delete this ${isPoster ? 'poster' : 'talk'}?`}
        open={Boolean(deletedTalkId)}
        closeModal={() => setDeletedTalkId('')}
        handleConfirm={handleDeleteTalk}
        inProgress={performingAction}
      />
    </>
  )
}

export default TalkListing
